import type { BoxProps } from '@withjoy/joykit';
import { Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollContainer, ScrollContent } from './VerticalScrollMessages.styles';

interface VerticalScrollMessagesProps
  extends Readonly<{
    height: BoxProps['height'];
    messages: ReadonlyArray<string>;
    shouldPauseOnMessage?: boolean;
  }> {}

export const VerticalScrollMessages = ({ messages, height, shouldPauseOnMessage = false }: VerticalScrollMessagesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(0);

  const messageCount = messages.length;

  useEffect(() => {
    const observer = createSafeResizeObserver(() => {
      if (scrollRef.current) {
        setScrollHeight(scrollRef.current.scrollHeight);
      }
    });

    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <ScrollContainer
      ref={scrollRef}
      overflow={'hidden'}
      position={'relative'}
      height={height}
    >
      {scrollHeight > 0 && (
        <ScrollContent
          messageCount={messageCount}
          scrollHeight={scrollHeight / 2}
          flexDirection={'column'}
          shouldPauseOnMessage={shouldPauseOnMessage}
        >
          {/* Duplicate messages once to enable seamless infinite scroll, to ensure there's no visual jump or glitch in the scroll. */}
          {[...messages, ...messages].map((msg, idx) => (
            <Flex
              key={`${idx}`}
              minHeight={pxToRem(48)}
              paddingY={4}
              alignItems={'center'}
              typographyVariant={'button2'}
              color="mono14"
            >
              {msg}
            </Flex>
          ))}
        </ScrollContent>
      )}
    </ScrollContainer>
  );
};
